import { useEffect, useId, useState } from "react";
import { Box, Button, Skeleton, TextField, Typography, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import dayjs from "dayjs";
import { useAppSelector } from "State/hooks";
import { useGetMeQuery } from "Services/user";
import { useGetDealInsightsMutation } from "Services/deal-insights";
import { DealInsightsResult } from "Models/deal-insights-result";
import { FunnelStage } from "Models/insights-result";
import { DateType } from "Models/filter-types";
import { DateTypeToBackend } from "Models/filter-mappers";
import { advisor, InfoHint } from "Components/AdvisorTheme";
import { formatCreatedAt } from "Components/TodaysBrief";
import { PipelineSelection } from "./usePipelineSelection";

const PLOT_HEIGHT = 150;
const MIN_COLUMN = 84;

/** Compact column chart; scrolls inside its card when stages outgrow the width. */
function StageColumns({ stages }: { stages: FunnelStage[] }) {
  const maximum = Math.max(1, ...stages.map(s => s.count));
  const summary = stages.map(s => `${s.label}: ${s.count}`).join(", ");
  return (
    <Box sx={{ overflowX: "auto", overflowY: "hidden", pb: 0.5 }}>
      <Box
        role="img"
        aria-label={`Column chart of deals that entered each stage. ${summary}`}
        sx={{ display: "flex", justifyContent: "center", gap: { xs: 1, sm: 2 }, minWidth: stages.length * MIN_COLUMN, px: 0.5 }}
      >
        {stages.map((s, i) => (
          <Box key={i} sx={{ flex: "1 1 0", minWidth: MIN_COLUMN - 8, maxWidth: 116, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ height: PLOT_HEIGHT + 22, width: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", borderBottom: `1px solid ${advisor.border}` }}>
              <Typography aria-hidden sx={{ fontSize: 13, fontWeight: 700, color: advisor.text, mb: 0.5 }}>{s.count.toLocaleString()}</Typography>
              <Box
                sx={{
                  width: "min(56px, 72%)",
                  height: Math.max(0, s.count) / maximum * PLOT_HEIGHT,
                  minHeight: s.count > 0 ? 4 : 0,
                  bgcolor: advisor.series[i % advisor.series.length],
                  borderRadius: "6px 6px 2px 2px",
                }}
              />
            </Box>
            <Typography
              aria-hidden
              title={s.label}
              sx={{
                mt: 0.75,
                fontSize: 12,
                lineHeight: 1.3,
                color: advisor.muted,
                textAlign: "center",
                width: "100%",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflowWrap: "break-word",
                hyphens: "auto",
                minHeight: 31,
              }}
            >
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Readable creation-date range: "Sep 1–30, 2026", "Aug 15 – Sep 30, 2026" or
 * "Dec 15, 2025 – Jan 10, 2026".
 */
export function formatCohortRange(start: string, end: string): string {
  const a = dayjs(start), b = dayjs(end);
  if (a.isSame(b, "day")) return a.format("MMM D, YYYY");
  if (a.isSame(b, "month")) return `${a.format("MMM D")}–${b.format("D, YYYY")}`;
  if (a.isSame(b, "year")) return `${a.format("MMM D")} – ${b.format("MMM D, YYYY")}`;
  return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`;
}

/** Heading that names the actual cohort: deals created in the range, by stages they entered. */
export function cohortTitle(start: string, end: string, today = dayjs()): string {
  const lastMonth = today.subtract(1, "month");
  const isLastMonth = start === lastMonth.startOf("month").format("YYYY-MM-DD") && end === lastMonth.endOf("month").format("YYYY-MM-DD");
  return `Stages reached by deals created ${isLastMonth ? "last month" : formatCohortRange(start, end)}`;
}

export default function RevenueCohort({ selection }: { selection: PipelineSelection }) {
 const companyId = useAppSelector(s => s.company.companyId);
 const { data: profile, isError: profileError, refetch: retryProfile } = useGetMeQuery();
 const { pipeline, isError: pipelineError, retry: retryPipelines } = selection;
 // The deal-insights aggregates are keyed by deal creation day; the API does not apply dateType.
 const dateType = DateType.CREATE;
 const [start, setStart] = useState(() => dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"));
 const [end, setEnd] = useState(() => dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD"));
 const [retry, setRetry] = useState(0);
 const [result, setResult] = useState<DealInsightsResult>();
 const [resultScope, setResultScope] = useState("");
 const [error, setError] = useState(false);
 const [loading, setLoading] = useState(false);
 const [showFilters, setShowFilters] = useState(false);
 const [showData, setShowData] = useState(false);
 const filtersId = useId();
 const dataId = useId();
 const [getInsights] = useGetDealInsightsMutation();
 const scope = [companyId, pipeline?.sourcePipelineId, dateType, start, end].join("|");
 const visibleResult = profile?.companyId === companyId && resultScope === scope ? result : undefined;
 useEffect(() => { setResult(undefined); }, [companyId]);
 useEffect(() => {
  let active = true;
  setResult(undefined); setError(false); setLoading(false);
  if (!companyId || profile?.companyId !== companyId || !pipeline || !start || !end || start > end) return;
  setLoading(true);
  const request = getInsights({ companyId, params: { pipelineId: pipeline.sourcePipelineId, dateType: DateTypeToBackend[dateType], dateRange: { startDate: dayjs(start).startOf("day").toISOString(), endDate: dayjs(end).endOf("day").toISOString() } } });
  request.unwrap().then(data => { if (active) { setResult(data); setResultScope(scope); } }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
  return () => { active = false; request.abort(); };
 }, [companyId, profile?.companyId, pipeline?.sourcePipelineId, dateType, start, end, retry, getInsights, scope]);
 const stages = visibleResult?.metrics?.dealFunnel?.stages ?? [];
 const range = start && end ? formatCohortRange(start, end) : "No range";
 const title = start && end && start <= end ? cohortTitle(start, end) : "Stages reached by deals created in a date range";
 const fieldSx = { minWidth: 0, "& .MuiInputBase-root": { fontSize: 14, bgcolor: "#FFFFFF" } };

 return <Box component="section" aria-labelledby="stages-title" sx={{ bgcolor: advisor.card, border: `1px solid ${advisor.border}`, borderRadius: advisor.radius, boxShadow: advisor.shadow, p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
   <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
     <Typography id="stages-title" component="h2" sx={{ fontSize: 18, fontWeight: 700, color: advisor.text, overflowWrap: "anywhere" }}>{title}</Typography>
     <InfoHint label="About this chart">
      Source: deal insights for {pipeline?.label ?? "the selected pipeline"}{visibleResult?.timestamp ? `, retrieved ${formatCreatedAt(visibleResult.timestamp)}` : ""}.
      The cohort is deals created {range} (by deal creation date). Each column counts the deals from that cohort that entered the stage,
      so one deal is counted in every stage it reached; columns do not show where deals sit now and do not add up to a deal total.
      This is why the numbers differ from the revenue snapshot’s Deal Count, which counts all deals currently in the pipeline,
      including closed stages. Currency totals are omitted because this response does not specify a currency convention.
     </InfoHint>
    </Box>
    <Typography sx={{ fontSize: 13, color: advisor.muted, overflowWrap: "anywhere" }}>
     {pipeline?.label ?? "No pipeline selected"} · deals counted in each stage they entered
    </Typography>
   </Box>
   <Button size="small" variant="outlined" startIcon={<TuneRoundedIcon />} aria-expanded={showFilters} aria-controls={filtersId} onClick={() => setShowFilters(v => !v)}>Dates</Button>
  </Box>
  {showFilters && <Box id={filtersId} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, mt: 1.5, p: 1.5, bgcolor: advisor.page, borderRadius: "10px" }}>
   <TextField size="small" type="date" label="Created from" value={start} onChange={e => setStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
   <TextField size="small" type="date" label="Created to" value={end} onChange={e => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
  </Box>}
  <Box sx={{ mt: 2 }}>
   {start > end && <Typography role="alert" sx={{ fontSize: 14 }}>Start date must precede end date.</Typography>}
   {profileError && <Typography sx={{ fontSize: 14 }}>Profile unavailable; the chart waits for your pipeline defaults. <Button size="small" onClick={() => retryProfile()}>Retry profile</Button></Typography>}
   {(error || pipelineError) && <Box role="alert" sx={{ fontSize: 14 }}>Deal insights unavailable. Access or subscription restrictions may apply. <Button size="small" onClick={() => { if (pipelineError) retryPipelines(); setRetry(n => n + 1); }}>Retry chart</Button></Box>}
   {loading && <Box role="status" aria-label="Loading filtered cohort" sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-around", height: PLOT_HEIGHT + 50 }}>{[0.9, 0.6, 0.45, 0.3, 0.2].map((h, i) => <Skeleton key={i} variant="rounded" width={40} height={h * PLOT_HEIGHT} />)}</Box>}
   {!loading && !error && !profileError && start <= end && !stages.length && <Typography sx={{ fontSize: 14, color: advisor.muted }}>No stage data available for this cohort.</Typography>}
   {!loading && !!stages.length && <>
    <StageColumns stages={stages} />
    <Button size="small" startIcon={<TableChartOutlinedIcon />} aria-expanded={showData} aria-controls={dataId} onClick={() => setShowData(v => !v)} sx={{ mt: 1 }}>
     {showData ? "Hide data" : "View data"}
    </Button>
    {showData && <Box id={dataId} sx={{ overflowX: "auto" }}><Table size="small" aria-label="Filtered deal cohort stage counts"><TableHead><TableRow><TableCell>Stage</TableCell><TableCell align="right">Deals that entered stage</TableCell></TableRow></TableHead><TableBody>{stages.map((s, i) => <TableRow key={i}><TableCell sx={{ overflowWrap: "anywhere" }}>{s.label}</TableCell><TableCell align="right">{s.count}</TableCell></TableRow>)}</TableBody></Table></Box>}
   </>}
  </Box>
 </Box>;
}
